import React, { useCallback, useState } from 'react';
import Cropper from 'react-easy-crop';
import { Area } from 'react-easy-crop/types';
import { Typography } from '@mui/material';
import TabsButton from 'components/Buttons/TabsButton';
import ButtonWithArrowIcon from 'components/Buttons/ButtonWithArrowIcon';
import StyledSliderV2 from 'components/Sliders/StyledSliderV2';
import getCroppedImg from './utils';

import styles from './styles.module.scss';
import { removeDocumentFile } from '../../../../local-store/idb/document-file-store';

type Crop = {
  x: number;
  y: number;
};

const defaultCrop = {
  x: 0,
  y: 0,
};

type InputProps = {
  fileUrl: string;
  aspectRatio?: number;
  onSave: (file: Blob) => void;
  onClose: () => void;
};

type Config = {
  aspect: number;
  cropShape: 'rect' | 'round';
};

const config: Config = {
  aspect: 1,
  cropShape: 'rect',
};

export const DocumentImageCropper = ({
  fileUrl,
  aspectRatio,
  onSave,
  onClose,
}: InputProps) => {
  const [crop, setCrop] = useState<Crop>(defaultCrop);
  const [zoom, setZoom] = useState<number>(1);
  const [brightness, setBrightness] = useState<number>(1);
  const [rotation, setRotation] = useState<number>(0);
  const [ratio, setRatio] = useState<number>(aspectRatio || config.aspect);
  const [croppedAreaPixels, setCroppedAreaPixels] = useState<Area | null>(null);

  const onCropChange = (crop: Crop) => {
    setCrop(crop);
  };

  const onZoomChange = (zoom: number) => {
    setZoom(zoom);
  };

  const onCropComplete = useCallback(
    (croppedArea: Area, croppedAreaPixels: Area) => {
      setCroppedAreaPixels(croppedAreaPixels);
    },
    []
  );

  const showCroppedImage = useCallback(async () => {
    try {
      if (croppedAreaPixels) {
        const croppedImage = await getCroppedImg(
          fileUrl,
          croppedAreaPixels,
          rotation,
          brightness
        );
        if (croppedImage) {
          // Remove image from cache
          await removeDocumentFile(fileUrl);
          onSave(croppedImage);
        } else {
          // TODO
        }
      }
    } catch (e) {
      console.error(e);
    }
  }, [croppedAreaPixels, rotation, brightness]);

  return (
    <div className={styles.root}>
      <div className={styles.cropContainerWrapper}>
        <div className={styles.actions}>
          <TabsButton disabled={true}>Editing</TabsButton>
          <div>
            <TabsButton onClick={onClose}>Back</TabsButton>
            <ButtonWithArrowIcon size="small" onClick={showCroppedImage}>
              Save
            </ButtonWithArrowIcon>
          </div>
        </div>
        <div className={styles.cropContainer}>
          <Cropper
            image={fileUrl}
            crop={crop}
            zoom={zoom}
            rotation={rotation}
            restrictPosition={false}
            aspect={ratio}
            cropShape={config.cropShape}
            showGrid={false}
            onCropChange={onCropChange}
            onCropComplete={onCropComplete}
            onZoomChange={onZoomChange}
            onRotationChange={setRotation}
            style={{
              cropAreaStyle: {
                border: '2px solid #35C7FF',
                borderRadius: '3px',
                boxShadow: '0px 0px 300px 100px rgb(255, 255, 255)',
              },
              mediaStyle: {
                filter: `brightness(${brightness})`,
              },
            }}
          />
        </div>
      </div>
      <div className={styles.controls}>
        <div className={styles.sliderContainer}>
          <Typography
            id="label-ratio"
            variant="overline"
            classes={{ root: styles.sliderLabel }}
          >
            Ratio
          </Typography>
          <StyledSliderV2
            value={ratio}
            min={0.01}
            max={10}
            step={0.01}
            aria-labelledby="label-ratio"
            classes={{ root: styles.slider }}
            onChange={(e, ratio) => setRatio(Number(ratio))}
          />
        </div>
        <div className={styles.sliderContainer}>
          <Typography
            id="label-rotation"
            variant="overline"
            classes={{ root: styles.sliderLabel }}
          >
            Rotate Image
          </Typography>
          <StyledSliderV2
            value={rotation}
            min={0}
            max={360}
            step={1}
            aria-labelledby="label-rotation"
            classes={{ root: styles.slider }}
            onChange={(e, rotation) => setRotation(Number(rotation))}
          />
        </div>
        <div className={styles.sliderContainer}>
          <Typography
            id="label-zoom"
            variant="overline"
            classes={{ root: styles.sliderLabel }}
          >
            Zoom Image
          </Typography>
          <StyledSliderV2
            value={zoom}
            min={0}
            max={3}
            step={0.01}
            aria-labelledby="label-zoom"
            classes={{ root: styles.slider }}
            onChange={(e, zoom) => setZoom(Number(zoom))}
          />
        </div>
        <div className={styles.sliderContainer}>
          <Typography
            id="label-brightness"
            variant="overline"
            classes={{ root: styles.sliderLabel }}
          >
            Brightness
          </Typography>
          <StyledSliderV2
            value={brightness}
            min={0}
            max={2}
            step={0.1}
            aria-labelledby="label-brightness"
            classes={{ root: styles.slider }}
            onChange={(e, brightness) => setBrightness(Number(brightness))}
          />
        </div>
      </div>
    </div>
  );
};
